/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TOOLTIP_UPDATES_INVENTORY_ACTIONS
 * Date:         2026-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Added tooltips to Edit and Delete action buttons.
 *
 * File Size Check:
 * - Current: 210 lines
 * - Adding: ~10 lines
 * - Projected: 220 lines
 * - Decision: Proceed
 *
 * Related Files:
 * - client/src/constants/tooltips.ts
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    EXTRACT_INVENTORY_ITEM_CARD
 * Date:         2025-12-11
 * Modified By:  Cursor
 *
 * Description:
 * Extracted inventory item card from InventoryPage.tsx into reusable component
 * for use in both personal inventory and global search pages.
 *
 * Related Screens / Modules:
 * - client/src/components/inventory/InventoryItemCard.tsx
 * - client/src/features/inventory/InventoryPage.tsx
 */

import React from "react";
import { Inventory } from "../../interfaces/Inventory";
import { ACTION_BUTTON_TOOLTIPS } from "../../constants/tooltips";
// [2026-02-13] CURRENCY_PHASE2: Centralized single-price display
import { formatCurrency } from "../../utils/priceDisplay";
import "./InventoryItemCard.scss";

// Helper function for condition badge colors - handles all condition types
const condClass = (s?: string): string => {
  const condition = (s || "").toLowerCase().trim();
  if (condition.includes("new retail") || condition.includes("new bulk")) {
    return "badge-new";
  }
  if (condition.includes("refurbished")) {
    return "badge-refurbished";
  }
  if (condition.includes("used")) {
    return "badge-used";
  }
  if (condition.includes("as-is") || condition.includes("as is")) {
    return "badge-as-is";
  }
  return "badge-neutral";
};

// Format relative time (without "Added" prefix)
const rel = (d?: string) => {
  const t = d ? new Date(d).getTime() : Date.now();
  const diff = Date.now() - t;
  const m = Math.floor(diff / 60000),
    h = Math.floor(diff / 3600000),
    day = Math.floor(diff / 86400000);
  return day > 0
    ? `${day} day${day > 1 ? "s" : ""} ago`
    : h > 0
      ? `${h} hour${h > 1 ? "s" : ""} ago`
      : `${Math.max(1, m)} min ago`;
};

// Extract seller company name from item (handles both seller and sellerId populated)
const getSellerCompanyName = (item: Inventory): string | undefined => {
  // Check seller populated field first
  if (item.seller && typeof item.seller === "object" && item.seller.companyName) {
    return item.seller.companyName;
  }
  // Check sellerId if it's populated as an object
  if (
    item.sellerId &&
    typeof item.sellerId === "object" &&
    !Array.isArray(item.sellerId) &&
    (item.sellerId as any).companyName
  ) {
    return (item.sellerId as any).companyName;
  }
  return undefined;
};

// [2025-12-11] Item card: Reusable item card component interface
export interface InventoryItemCardProps {
  item: Inventory;
  showSellerInfo?: boolean;
  onEdit?: (item: Inventory) => void;
  onDelete?: (item: Inventory) => void;
  onView?: (item: Inventory) => void;
  onClick?: (item: Inventory) => void;
  renderActions?: (item: Inventory) => React.ReactNode;
  isDeleting?: boolean;
  // Optional: Display mode - 'row' only (card mode removed as unused)
  displayMode?: "row";
  // Disable meta information hover hide behavior (used in search results)
  disableMetaHoverHide?: boolean;
}

// [2025-12-11] Item card: Main item card component
export function InventoryItemCard({
  item,
  showSellerInfo = false,
  onEdit,
  onDelete,
  onView,
  onClick,
  renderActions,
  isDeleting = false,
  displayMode = "row",
  disableMetaHoverHide = false,
}: InventoryItemCardProps) {
  // Two-row display mode matching Figma design
  const sellerCompanyName = showSellerInfo ? getSellerCompanyName(item) : undefined;

  return (
    <div
      className={`inv-row py-3 px-3 border-bottom${disableMetaHoverHide ? " inv-row--no-hide-meta" : ""} inv-row--mobile`}
      onClick={(e) => {
        if (onClick) {
          e.preventDefault();
          onClick(item);
        }
      }}
    >
      {/* Top Row */}
      <div className="row">
        {/* Column 1: Title */}
        <div className="col-12 col-md-5">
          <div className="u-H4-18-bold text-color-black">{item.title}</div>
        </div>
        {/* Column 2: Category */}
        <div className="col-6 col-md-3 inv-row__meta">
          <div className="u-P3-14-medium text-color-black">{item.category || "-"}</div>
        </div>
        {/* Column 3: QTY */}
        <div className="col-6 col-md-2 inv-row__meta">
          <div className="u-P3-14-medium text-color-black">QTY: {item.quantity}</div>
        </div>
        {/* Column 4: Condition badge */}
        <div className="col-6 col-md-2 inv-row__meta">
          <span className={`badge ${condClass(item.condition)}`}>{item.condition}</span>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="row mt-2">
        {/* Column 1: Description */}
        <div className="col-12 col-md-5">
          {item.description && (
            <div className="u-P3-14-medium text-color-grey-1 clamp-2">{item.description}</div>
          )}
        </div>
        {/* Column 2: Seller */}
        <div className="col-6 col-md-3 inv-row__meta">
          {showSellerInfo && sellerCompanyName && (
            <div className="u-Label-12-medium text-color-grey-2-alt">{sellerCompanyName}</div>
          )}
        </div>
        {/* Column 3: Date */}
        <div className="col-6 col-md-2 inv-row__meta">
          <div className="u-Label-12-medium text-color-grey-1">{rel(item.createdAt)}</div>
        </div>
        {/* Column 4: Price - user's preferred currency */}
        <div className="col-6 col-md-2 inv-row__meta inv-row__price">
          <div className="u-P1-18-semi-bold text-color-black tabular-nums">
            {formatCurrency(item.price, item.currency, (item as any).convertedPrice, (item as any).displayCurrency) || "N/A"}
          </div>
        </div>
      </div>

      {/* Row actions - Figma quick edit buttons (node-id=907-3264 / 907-3287) */}
      {(onEdit || onDelete) && (
        <div className="position-absolute end-0 top-50 translate-middle-y pe-3">
          <div className={`inv-row__actions ${isDeleting ? "opacity-50" : ""}`}>
            {onEdit && (
              <button
                className="btn-black btn-sm inv-row__action-btn"
                aria-label={`Edit ${item?.title ?? "item"}`}
                tabIndex={0}
                title={ACTION_BUTTON_TOOLTIPS.EDIT_ITEM}
                onClick={(e) => {
                  e.stopPropagation();
                  if (item._id) {
                    onEdit(item);
                  }
                }}
              >
                Edit
              </button>
            )}
            {onDelete && (
              <button
                className="btn-black btn-sm inv-row__action-btn"
                aria-label={`Delete ${item?.title ?? "item"}`}
                tabIndex={0}
                title={ACTION_BUTTON_TOOLTIPS.DELETE_ITEM}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(item);
                }}
                disabled={isDeleting}
              >
                Delete
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
